import React, { Component } from 'react';
import { Helmet } from 'react-helmet';
import styled from '@emotion/styled';
import SearchBars from 'components/SearchBars';
import { observer, inject } from 'mobx-react';
import Card from 'components/Card';
import * as DataList from 'components/DataList';
import ProjectSidebar from '../projects/project-sidebar';

const PageWrapGray = styled('div')`
  padding: 12px;
  background-color: #F8FAFB;
  min-height: calc(100vh - 75px);
`

const COLUMNS = [
  { area: 'number', label: 'PO number', width: 'minmax(130px, 0.8fr)', bone: 'single' },
  { area: 'supplier', label: 'Supplier', width: 'minmax(200px, 1.6fr)', bone: 'short', icon: 'shop' },
  { area: 'items', label: 'Items', width: '90px', bone: 'single' },
  { area: 'status', label: 'Status', width: '150px', bone: 'pill' },
  { area: 'actions', label: '', width: '40px', bone: 'none' },
];

const CARDS = {
  columns: 'minmax(0, 1fr) auto auto',
  areas: [
    'number status actions',
    'supplier supplier supplier',
    'items items items',
  ],
};

const MIN_TABLE_WIDTH = 640;

const NOUN = 'purchase orders';

// Defaults from viewStore.purchaseOrderItemSearch.
const DEFAULT_WHERE = {
  material_line_item_id: '',
  supplier_id: '',
  status: [],
  items_count: '',
};

const FILTERS = {
  supplier_id: 'supplier',
  material_line_item_id: 'material item',
  status: 'status',
  items_count: 'number of items',
};

// Same statuses as the material items on the order.
const STATUS_TONES = {
  'pending order': 'amber',
  ordered: 'blue',
  'in transit': 'purple',
  received: 'green',
  'picked up by the technician': 'green',
  archived: 'grey',
};

const showPath = (purchaseOrder) =>
  `/dashboard/purchase-orders/${purchaseOrder.attributes.id}/show-purchase-order`;

@inject('store', 'workReportStore', 'viewStore') @observer
class PurchaseOrders extends Component {

  componentDidMount() {
    this.props.workReportStore.loadPurchaseOrders();
  }

  // Empty list's "Clear filters": back to all purchase orders, and remount the
  // filter bar so the supplier picker forgets its selection.
  clearFilters = () => {
    const { viewStore, workReportStore } = this.props;
    viewStore.purchaseOrderItemSearch = {
      ...viewStore.purchaseOrderItemSearch,
      term: '',
      where: { ...DEFAULT_WHERE },
      page: 1,
    };
    viewStore.bumpListFilters();
    viewStore.PurchaseOrder();
    workReportStore.loadPurchaseOrders();
  }

  renderList() {
    const { viewStore } = this.props;
    const { objects } = this.props.workReportStore.purchaseOrderItem;
    const rows = (objects || []).filter((purchaseOrder) => purchaseOrder.attributes.purchase_order_number);

    if (rows.length === 0) {
      return (
        <DataList.Empty
          noun={NOUN}
          filters={DataList.activeFilters(viewStore.purchaseOrderItemSearch.where, FILTERS)}
          term={viewStore.purchaseOrderItemSearch.term}
          onClearFilters={this.clearFilters}
        />
      );
    }

    return (
      <DataList.List
        columns={COLUMNS}
        cards={CARDS}
        minTableWidth={MIN_TABLE_WIDTH}
        total={rows.length}
        noun={NOUN}
        note={DataList.sortNote(viewStore.purchaseOrderItemSearch.order)}
      >
        {rows.map((purchaseOrder) => {
          const attributes = purchaseOrder.attributes;
          const supplier = attributes.supplier || {};
          const items = attributes.material_line_items;
          const status = attributes.status;
          return (
            <DataList.Row key={purchaseOrder.id || attributes.id}>
              <DataList.Cell area="number">
                <DataList.RowLink
                  href={showPath(purchaseOrder)}
                  label={`Purchase order ${attributes.purchase_order_number}`}
                >
                  {attributes.purchase_order_number}
                </DataList.RowLink>
              </DataList.Cell>
              <DataList.Cell area="supplier">
                {supplier.id ? (
                  <DataList.Lines
                    primary={supplier.name}
                    href={`/dashboard/suppliers/${supplier.id}/show`}
                    secondary={supplier.email || supplier.phone}
                  />
                ) : (
                  <DataList.Muted>No supplier</DataList.Muted>
                )}
              </DataList.Cell>
              <DataList.Cell area="items">
                {Array.isArray(items) ? (
                  <DataList.Lines
                    primary={`${items.length} ${items.length === 1 ? 'item' : 'items'}`}
                    strong={false}
                  />
                ) : (
                  <DataList.Muted>—</DataList.Muted>
                )}
              </DataList.Cell>
              <DataList.Cell area="status">
                {status ? (
                  <DataList.Pill tone={STATUS_TONES[status] || 'grey'}>
                    {`${status.charAt(0).toUpperCase()}${status.slice(1)}`}
                  </DataList.Pill>
                ) : (
                  <DataList.Muted>No status</DataList.Muted>
                )}
              </DataList.Cell>
              <DataList.Cell area="actions">
                <DataList.Actions
                  items={[
                    {
                      icon: 'edit',
                      label: 'Open purchase order',
                      onClick: () => viewStore.ShowPurchaseOrder({ uuid: purchaseOrder.id }),
                    },
                  ]}
                />
              </DataList.Cell>
            </DataList.Row>
          );
        })}
      </DataList.List>
    );
  }

  render() {
    const { workReportStore, viewStore } = this.props;
    const { isLoading, isError } = workReportStore.purchaseOrderItem;

    let content;
    if (isLoading) {
      content = (
        <DataList.Loading columns={COLUMNS} cards={CARDS} minTableWidth={MIN_TABLE_WIDTH} noun={NOUN} />
      );
    } else if (isError) {
      content = <DataList.LoadError noun={NOUN} onRetry={() => workReportStore.loadPurchaseOrders()} />;
    } else {
      content = this.renderList();
    }

    return (
      <div>
        <Helmet><title>Purchase Orders</title></Helmet>
        <div className="row no-gutters">
          <div className="col-lg-2">
            <ProjectSidebar />
          </div>
          <div className="col-lg-10">
            <PageWrapGray>
              <SearchBars.PurchaseOrders key={viewStore.listFiltersVersion} />
              <Card>
                {content}
              </Card>
            </PageWrapGray>
          </div>
        </div>
      </div>
    )
  }

};

export default PurchaseOrders;
